import { html, LitElement, nothing } from 'lit'
import { customElement, property, state } from 'lit/decorators.js'
import { consume } from '@lit/context'
import { PDP_PRODUCT_STORE, type PdpProductStore } from 'components/discovery/pdp/pdp-product/pdp-product-store.js'
import { inject } from '@cloudsuite/mosaic-shared/scripts/dependency-injection.js'
import { createEffect } from '@cloudsuite/mosaic-shared/scripts/signals.js'
import type { Levy } from 'components/discovery/pdp/typing/pdp-product.js'
import { productTileProductContext } from 'components/discovery/product/tile-parts/tile-product-context.js'
import { InternalProduct } from 'components/discovery/product/tile-parts/tile-parts-typing.js'
import { map } from 'lit/directives/map.js'
import { t } from '@cloudsuite/mosaic-shared/translations/translator.js'
import { styles } from './levies.style.js'

export const fontVariants = ['body', 'body-s', 'caption-l', 'caption-m', 'caption-s'] as const
export type fontVariantsType = typeof fontVariants

export const levyListVariants = ['unstyled', 'bullet', 'custom-icon'] as const
export type levyListVariantsType = typeof levyListVariants

@customElement('csc-levies')
export class CSCLevies extends LitElement {
  static override styles = [styles]

  @inject(PDP_PRODUCT_STORE)
  private store!: PdpProductStore

  @property({ type: Array })
  levies: Levy[] = []

  @property({ attribute: 'font-variant' })
  fontVariant: fontVariantsType[number] = 'body-s'

  @property({ attribute: 'list-variant' })
  listVariant: levyListVariantsType[number] = 'unstyled'

  @property({ attribute: 'list-icon-name' })
  listIconName = 'star'

  @property({ attribute: 'list-icon-style' })
  listIconStyle = 'regular'

  @consume({ context: productTileProductContext })
  @state()
  private _tileProduct: InternalProduct | undefined = undefined

  private get effectiveLevies(): { name: string; amount: string }[] {
    if (this._tileProduct) return this._tileProduct.levies.filter(levy => levy.amountNoFormat != null)
    return this.levies.filter(levy => levy.amount_no_format != null)
  }

  constructor() {
    super()
    createEffect(() => {
      const product = this.store.product()
      if (product) this.levies = product.levies
    })
  }

  override render() {
    const levies = this.effectiveLevies
    if (levies.length <= 0) return nothing

    return html`<cse-list class="csc-levies">
      ${map(levies, (levy) => {
        return html`
          <cse-list-item variant="${this.listVariant}" icon-name="${this.listIconName}" icon-style="${this.listIconStyle}">
            <cse-text variant="${this.fontVariant}" class="levy">${t('levy_text', { ns: 'csc-levies', default: 'Excl. {{variable_name}} of {{variable_price}}',  variable_name: levy.name, variable_price: levy.amount })}</cse-text>
          </cse-list-item>
        `
      })}
    </cse-list>`
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'csc-levies': CSCLevies
  }
}
